import { gql } from "@apollo/client";
import { PopoverInteractionKind } from "@blueprintjs/core";
import { OrgRole, UNLIMITED_AI_CREDITS_SENTINEL } from "@hex/common";
import React from "react";
import { styled } from "styled-components";

import { Flex, HexTooltip } from "../../hex-components";
import { useCurrentUserIsRoleOrAbove } from "../../hooks/me/useCurrentUserIsRoleOrAbove";
import { ORG_ID } from "../../orgs";
import { useHexFlag } from "../../util/useHexFlags";

import { BasicCreditMeter } from "./BasicCreditMeter";
import { CreditExplanation } from "./CreditExplanation";
import { useGetAiCreditMeterDetailsQuery } from "./WorkspaceDropdownCreditMeter.generated";

gql`
  query GetAiCreditMeterDetails($orgId: OrgId!) {
    orgById(orgId: $orgId) {
      id
      aiBillingEnforcementDate
      agentUsageDetails {
        orgId
        creditTopUpsEnabled
        totalWorkspaceCreditPool
        workspaceCreditsUsed
        creditSpendLimitCents
        creditResetDate
      }
    }
    me {
      id
      aiUsageLimitDetails {
        userId
        totalUserCreditsAvailable
        totalUserCreditsUsed
      }
    }
  }
`;

export const WorkspaceDropdownCreditMeter = React.memo(
  function WorkspaceDropdownCreditMeter() {
    const { data } = useGetAiCreditMeterDetailsQuery({
      variables: {
        orgId: ORG_ID,
      },
    });
    const isUserExplorerEditorOrAdmin = useCurrentUserIsRoleOrAbove(
      OrgRole.EXPLORER,
    );

    const aiCreditGrantsFlagEnabled = useHexFlag("ai-credit-grants");

    if (
      // If the grants flag is not enabled, other aspects of credits are also disabled
      // and therefore showing credit information would be confusing.
      !aiCreditGrantsFlagEnabled ||
      // Only show the credit meter to Explorer+
      !isUserExplorerEditorOrAdmin ||
      // If the data is not loaded yet, or failed to load, we don't show the credit meter
      !data ||
      // The user may have some reason for not having limits enforced, such as BYOK
      !data.me?.aiUsageLimitDetails ||
      // If the enforcement date is not set, we don't show the credit meter, since we are gradually
      // rolling out
      data.orgById.aiBillingEnforcementDate == null
    ) {
      return null;
    }

    const { totalUserCreditsAvailable, totalUserCreditsUsed } =
      data.me.aiUsageLimitDetails;
    const endPeriodResetDate = new Date(
      data.orgById.agentUsageDetails.creditResetDate,
    );
    const enforcementDate = new Date(data.orgById.aiBillingEnforcementDate);

    const { totalWorkspaceCreditPool, workspaceCreditsUsed } =
      data.orgById.agentUsageDetails;
    return (
      <StyledHexTooltip
        content={
          <CreditExplanation
            enforcementDate={enforcementDate}
            topUpsEnabled={data.orgById.agentUsageDetails.creditTopUpsEnabled}
            userCreditsTotal={
              totalUserCreditsAvailable ?? UNLIMITED_AI_CREDITS_SENTINEL
            }
            workspaceCreditsAvailable={
              totalWorkspaceCreditPool - workspaceCreditsUsed > 0
            }
          />
        }
        interactionKind={PopoverInteractionKind.HOVER}
        position="right"
      >
        <Flex css="padding: 8px 8px 10px; flex: 1;">
          <BasicCreditMeter
            resetDate={endPeriodResetDate}
            totalCredits={
              totalUserCreditsAvailable == null
                ? UNLIMITED_AI_CREDITS_SENTINEL
                : totalUserCreditsAvailable
            }
            usedCredits={totalUserCreditsUsed}
          />
        </Flex>
      </StyledHexTooltip>
    );
  },
);

const StyledHexTooltip = styled(HexTooltip)`
  position: relative;

  && {
    display: flex;
  }
`;
